import { z } from 'zod';
import { currencyCodeSchema, uuidSchema } from './index';

// ==================== Group ====================

export const groupSchema = z.object({
  id: uuidSchema,
  name: z.string().min(1).max(100),
  description: z.string().nullable(),
  emoji: z.string().max(8).nullable(),
  image_url: z.string().url().nullable(),
  default_currency: currencyCodeSchema,
  allow_members_to_invite: z.boolean(),
  is_archived: z.boolean(),
  created_by: uuidSchema,
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type Group = z.infer<typeof groupSchema>;

export const groupCreateSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(1000).nullable().optional(),
  emoji: z.string().max(8).nullable().optional(),
  default_currency: currencyCodeSchema.default('EUR'),
  allow_members_to_invite: z.boolean().default(true),
});
export type GroupCreate = z.infer<typeof groupCreateSchema>;

export const groupUpdateSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(1000).nullable().optional(),
  emoji: z.string().max(8).nullable().optional(),
  default_currency: currencyCodeSchema.optional(),
  allow_members_to_invite: z.boolean().optional(),
  is_archived: z.boolean().optional(),
});
export type GroupUpdate = z.infer<typeof groupUpdateSchema>;

// ==================== Group Member ====================

export const groupRoleSchema = z.enum(['admin', 'member']);
export type GroupRole = z.infer<typeof groupRoleSchema>;

export const groupMemberSchema = z.object({
  id: uuidSchema,
  group_id: uuidSchema,
  participant_id: uuidSchema,
  role: groupRoleSchema,
  joined_at: z.string(),
  left_at: z.string().nullable(),
});
export type GroupMember = z.infer<typeof groupMemberSchema>;

// ==================== Invitation ====================

export const inviteRequestSchema = z.object({
  group_id: uuidSchema,
  display_name: z.string().min(1).max(100),
  email: z.string().email().optional(),
});
export type InviteRequest = z.infer<typeof inviteRequestSchema>;

export const inviteResponseSchema = z.object({
  participant_id: uuidSchema,
  invitation_id: uuidSchema.nullable(),
  is_new_invitation: z.boolean(),
  is_ghost: z.boolean().default(false),
});
export type InviteResponse = z.infer<typeof inviteResponseSchema>;

export const acceptResponseAuthenticatedSchema = z.object({
  authenticated: z.literal(true),
  group_id: uuidSchema,
  group_name: z.string(),
});

export const acceptResponseAnonymousSchema = z.object({
  authenticated: z.literal(false),
  group_id: uuidSchema,
  group_name: z.string(),
  group_emoji: z.string().nullable(),
  email_hint: z.string(),
});

export const acceptResponseSchema = z.union([
  acceptResponseAuthenticatedSchema,
  acceptResponseAnonymousSchema,
]);
export type AcceptResponse = z.infer<typeof acceptResponseSchema>;
